<template>
  <nav
    :class="[
      'fixed top-0 left-0 z-40 transition-all duration-300 flex items-center',
      isSolid ? 'bg-white shadow-md' : 'bg-black/20 backdrop-blur-sm',
      isCartPinned ? 'w-full lg:w-3/4 lg:right-auto' : 'w-full right-0'
    ]"
    :style="{ height: '80px', top: topOffset }"
  >
    <div class="relative w-full h-full">
      <div
        class="relative z-10 container mx-auto px-4 py-1 flex items-center justify-between h-full"
      >
        <div class="flex items-center gap-4 md:gap-6">
          <div class="flex items-center gap-3">
            <div
              v-if="logoUrl"
              :class="[
                'w-14 h-14 rounded-full flex items-center justify-center overflow-hidden shadow-lg transition-colors',
                isSolid ? 'bg-white' : 'bg-white/20'
              ]"
            >
              <img
                :src="logoUrl"
                :alt="storeName"
                class="w-full h-full object-contain"
              />
            </div>
            <div
              v-else
              :class="[
                'w-14 h-14 rounded-full flex items-center justify-center text-white font-bold font-serif text-2xl shadow-lg transition-colors',
                isSolid ? 'bg-primary-600' : 'bg-black'
              ]"
            >
              {{ displayStoreName.charAt(0).toUpperCase() }}
            </div>
            <h1
              :class="[
                'font-serif font-bold text-xl hidden sm:block',
                isSolid ? 'text-gray-900' : 'text-white'
              ]"
            >
              {{ displayStoreName }}
            </h1>
            <!-- Table name & order number (table orders) -->
            <div
              v-if="tableName"
              :class="[
                'flex items-center gap-2 pl-2 border-l border-gray-200 sm:border-gray-300',
                isSolid
                  ? 'text-gray-700 border-gray-200'
                  : 'text-white/90 border-white/40'
              ]"
            >
              <span class="text-sm sm:text-base font-semibold">
                Table {{ tableName }}
              </span>
              <span
                v-if="orderNumber"
                :class="[
                  'text-sm font-medium',
                  isSolid ? 'text-gray-500' : 'text-white/80'
                ]"
              >
                #{{ orderNumber }}
              </span>
            </div>
          </div>

          <!-- Location Selector / Address (parametrizable) -->
          <template v-if="showLocationSelector">
            <!-- Location Selector Trigger -->
            <LocationTrigger
              v-if="storesCount > 1"
              @click="$emit('location-click')"
              :location-name="selectedLocationName"
              :store-address="selectedLocationAddress"
              :stores-count="storesCount"
              :is-solid="isSolid"
            />
            <!-- Store Address (when only one store) -->
            <div
              v-else-if="selectedLocationAddress"
              :class="[
                'flex items-center gap-2 pl-2 pr-4 py-1.5 rounded-full md:ml-2',
                isSolid ? 'text-gray-600' : 'text-white'
              ]"
            >
              <div
                :class="[
                  'p-1.5 rounded-full',
                  isSolid
                    ? 'bg-gray-100 text-primary-600'
                    : 'bg-white/20 text-white'
                ]"
              >
                <MapPin :size="16" />
              </div>
              <span
                class="text-xs font-bold leading-tight truncate location-name"
              >
                {{ selectedLocationName || selectedLocationAddress }}
              </span>
            </div>
          </template>
        </div>

        <!-- Right Side: Cart -->
        <div class="flex items-center gap-3">
          <button
            @click="$emit('cart-click')"
            :class="[
              'relative flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all fadeIn-0-3s whitespace-nowrap',
              isCartPinned ? 'lg:hidden' : '',
              isSolid
                ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-md shadow-primary-600/20'
                : 'bg-white text-black hover:bg-gray-100'
            ]"
          >
            <ShoppingBag :size="18" />
            <span>Your Order</span>
            <span class="hidden sm:inline">{{ cartCount }} items</span>
            <span class="sm:hidden">{{ cartCount }}</span>
            <span
              v-if="hasCartUpdates"
              class="absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 animate-pulse z-10"
              :class="
                isSolid ? 'bg-red-500 border-white' : 'bg-red-500 border-white'
              "
              title="Your order has been updated"
            />
          </button>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { MapPin, ShoppingBag } from "lucide-vue";
import LocationTrigger from "./Location/LocationTrigger.vue";
import { formatLocationAddress } from "../utils/formatters";
export default {
  name: "Navbar",
  components: {
    LocationTrigger,
    MapPin,
    ShoppingBag
  },
  props: {
    cartCount: {
      type: Number,
      default: 0
    },
    selectedLocation: {
      type: Object,
      default: null
    },
    isCartPinned: {
      type: Boolean,
      default: false
    },
    hasCartUpdates: {
      type: Boolean,
      default: false
    },
    forceSolid: {
      type: Boolean,
      default: false
    },
    topOffset: {
      type: String,
      default: "0px"
    },
    storeName: {
      type: String,
      default: ""
    },
    storeLogo: {
      type: String,
      default: ""
    },
    storesCount: {
      type: Number,
      default: 0
    },
    showLocationSelector: {
      type: Boolean,
      default: true
    },
    tableName: {
      type: String,
      default: null
    },
    orderNumber: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      scrolled: false
    };
  },
  computed: {
    // Use selected location name if available, otherwise use store name prop
    displayStoreName() {
      const locationName = this.getLocationDisplayName(this.selectedLocation);
      if (locationName) {
        return locationName;
      }
      return this.storeName || "";
    },
    // Use selected location logo if available, otherwise use store logo prop
    logoUrl() {
      if (this.selectedLocation) {
        // Try different possible logo/image properties from location
        return (
          this.selectedLocation.logo ||
          this.selectedLocation.image ||
          this.selectedLocation.photo_url ||
          this.selectedLocation.photo_thumbnail ||
          this.storeLogo ||
          ""
        );
      }
      return this.storeLogo || "";
    },
    isSolid() {
      return this.scrolled || this.forceSolid;
    },
    // Get selected location name
    selectedLocationName() {
      return this.getLocationDisplayName(this.selectedLocation) || null;
    },
    // Format address for selected location
    selectedLocationAddress() {
      if (!this.selectedLocation) {
        return "";
      }
      return formatLocationAddress(this.selectedLocation);
    }
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    getStoreDisplayName(store) {
      return (
        store?.displayName ||
        store?.display_name ||
        store?.displayname ||
        store?.location_name ||
        store?.name ||
        ""
      );
    },
    getLocationDisplayName(location) {
      return (
        location?.displayName ||
        location?.display_name ||
        location?.displayname ||
        location?.location_name ||
        ""
      );
    },
    handleScroll() {
      this.scrolled = window.scrollY > 50;
    }
  }
};
</script>

<style scoped>
.location-name {
  max-width: 120px;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  overflow-wrap: anywhere;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fadeIn-0-3s {
  animation: fadeIn 0.3s ease-out;
}
</style>
